// Symmetric split-sleeper-berth tier. Canada and US Passenger share identical
// symmetric geometry and an identical algorithm; the only differences between the
// families are the violation-behavior values injected via state.intermediateSplitConfig
// (resolved per-family in intermediateSplitConfig.ts). So both families run this one
// evaluator — reverting a family's behavior is a matrix-value change, not a code path.
import {
    HOURS,
    getSplitPeriodDuration,
    type IState,
    type ISplitInfo,
    type TWorkdayResetInfo
} from "../helpers";
import { startViolation, getConfigWorkdayMaximum } from "../hosCalculator";
import { appendCurrentSplitLog, beginWorkdayRecalc, completeSplit, isSplitCompleteBlockedByViolation } from "./shared";

export const evaluateSymmetricSplit = (state: IState) => {
    let splitInfo = state.workday.splitSB;
    const splitConfig = state.config.splitConfig;
    if (!splitInfo || splitConfig?.type !== "symmetric") {
        return;
    }
    let { splitMinimum, splitTotal } = splitConfig;
    let prevLogWasSB = splitInfo.inSleeper;
    splitInfo.inSleeper = state.current.status === "SB";
    if (splitInfo.inSleeper) {
        collectSplitPeriodsSymmetric(state, splitInfo, prevLogWasSB);
        const workdayMaximum = getConfigWorkdayMaximum(state);
        // Split hours do not count towards workday.
        let latestSbPeriodLength = splitInfo.periods[0] && getSplitPeriodDuration(splitInfo.periods[0]);
        // Row B': skip freeze when violation prevents new freeze and a violation is active.
        if (workdayMaximum && state.workday.start && state.workday.end && latestSbPeriodLength && latestSbPeriodLength >= splitMinimum &&
            (!state.intermediateSplitConfig.violationPreventsNewFreeze || !isSplitCompleteBlockedByViolation(state))) {
            state.workday.end.setTime(state.workday.start.getTime() + workdayMaximum + latestSbPeriodLength);
        }
    } else {
        splitInfo.periods = splitInfo.periods.filter(o => getSplitPeriodDuration(o) >= splitMinimum);
        let totalSplitDuration = getCombinedSplitDurationSymmetric(splitInfo, splitMinimum);
        if (totalSplitDuration && totalSplitDuration < splitTotal) {
            splitInfo.periods.splice(getSplitPeriodDuration(splitInfo.periods[0]) < getSplitPeriodDuration(splitInfo.periods[1]) ? 0 : 1); // remove the smaller period
            state.workday.splitLogs = [];
        }
    }

    recordSplitLogsSymmetric(state, splitInfo);

    // Row A: driver cannot complete split if in violation and the family blocks completion on violation.
    if (state.intermediateSplitConfig.violationPreventsSplitCompletion && isSplitCompleteBlockedByViolation(state)) {
        return;
    }

    /* If split found, reset the counts and set workday.start/end from end of first SB split */
    let totalSplitDuration = getCombinedSplitDurationSymmetric(splitInfo, splitMinimum);
    if (totalSplitDuration && totalSplitDuration >= splitTotal) {
        state.workday.lastSBPeriodMinimum = getLastSBPeriodMinimum(
            getSplitPeriodDuration(splitInfo.periods[0]),
            splitMinimum,
            splitMinimum,
            splitTotal
        );

        // todo: probably move this into getCombinedSplitDurationSymmetric, like I did for property split by moving the logic into isSplitCompleteAsymmetric
        // find the completing (second) period for the symmetric split
        let workdayResetInfo: TWorkdayResetInfo | undefined;

        if (splitInfo.periods.length === 2) {
            // the second period is the completing period
            const secondPeriod = splitInfo.periods[1];
            const lastSBPeriodMin = state.workday.lastSBPeriodMinimum || splitMinimum;
            workdayResetInfo = {
                type: "symmetricSplitSleeperBerth",
                periods: [
                    {
                        type: "sleeperBerth",
                        fromDate: splitInfo.periods[0].fromDate,
                        toDate: splitInfo.periods[0].toDate
                    },
                    {
                        type: "sleeperBerth",
                        fromDate: splitInfo.periods[1].fromDate,
                        toDate: splitInfo.periods[1].toDate
                    }
                ],
                eligibleForResetDate: new Date(secondPeriod.fromDate.getTime() + lastSBPeriodMin)
            };
        }

        completeSplit(state, workdayResetInfo);
    }
};

// For symmetric (Canada / US Passenger) split drivers, recalculate Workday violations for the current workday.
// eslint-disable-next-line complexity
export const recalculateWorkdayViolationsForCurrentWorkdaySymmetric = (state: IState) => {
    // Note: unlike recalculateWorkdayViolationsForCurrentWorkdayAsymmetric, this function
    // intentionally does NOT call completeViolations(). The loop below breaks at the first
    // driving violation (startViolation is called with a bounded end, then break), so no
    // open-ended violation is ever left to finalize. The asymmetric recalc iterates every
    // log to completion and can leave an open violation, which is why it must close with
    // completeViolations().
    const splitConfig = state.config.splitConfig?.type === "symmetric" ? state.config.splitConfig : undefined;
    const recalc = beginWorkdayRecalc(state, !!state.workday.splitSB?.periods.length, !!splitConfig);
    if (!recalc || !splitConfig) {
        return;
    }
    const { workdayMaximum, splitLogs } = recalc;
    let workdayEnd = recalc.workdayEnd;
    let extended = state.config.bigDayBonus && state.workday.workdayExtended ? (workdayMaximum + state.workday.workdayExtended) / HOURS : 0;
    let longestSbPeriod = 0;
    let currentSbPeriod = 0;
    let isSplitBlocked =
        state.intermediateSplitConfig.violationRecalculatesWorkday && isSplitCompleteBlockedByViolation(state);
    for (let i = 0; i < splitLogs.length - 1; i++) {
        let log = splitLogs[i];
        let logEndDate = splitLogs[i + 1].dateTime;
        currentSbPeriod = log.status === "SB" ? currentSbPeriod + logEndDate.getTime() - log.dateTime.getTime() : 0;
        // check if workday should be extended by pairing the longestSbPeriod with splitSbDuringReset
        if (!isSplitBlocked && state.splitSBDuringReset && currentSbPeriod > longestSbPeriod) {
            let isSbPeriodLongEnoughToPair = currentSbPeriod >= splitConfig.splitMinimum;
            let isSbPeriodsLongEnoughToCompleteSplit = state.splitSBDuringReset + currentSbPeriod >= splitConfig.splitTotal;
            if (isSbPeriodLongEnoughToPair && isSbPeriodsLongEnoughToCompleteSplit) {
                workdayEnd += currentSbPeriod - longestSbPeriod;
            }
            longestSbPeriod = currentSbPeriod;
        }
        // check for violation
        if (log.status === "D") {
            // If D log goes past workdayEnd, start a violation
            if (logEndDate.getTime() > workdayEnd) {
                let fromDate = new Date(Math.max(log.dateTime.getTime(), workdayEnd));
                startViolation(state, log.id, "Workday", fromDate, logEndDate, extended);
                state.workday.end?.setTime(workdayEnd + extended);
                break;
            }
        }
    }
};

// collect consecutive SB counts. Can safely assume current log is SB
export const collectSplitPeriodsSymmetric = (state: IState, splitInfo: ISplitInfo, prevLogWasSB: boolean) => {
    if (prevLogWasSB) {
        splitInfo.periods[splitInfo.periods.length - 1].toDate = state.nextDate;
    } else {
        splitInfo.periods.push({ fromDate: state.currentDate, toDate: state.nextDate });
    }
    while (splitInfo.periods.length > 2) {
        splitInfo.periods.shift();
    }
};

export const getCombinedSplitDurationSymmetric = (splitInfo: ISplitInfo, splitMinimum: number) => {
    return splitInfo.periods.length === 2
        ? splitInfo.periods
              .map(p => getSplitPeriodDuration(p))
              .reduce(function (prev, curr) {
                  return curr >= splitMinimum ? prev + curr : prev;
              }, 0)
        : undefined;
};

// Given the duration of the first SB period, returns the duration of the second SB period rquired to complete the split
export const getLastSBPeriodMinimum = (period1: number, period1Minimum: number, period2Minimum: number, splitTotal: number): number | undefined => {
    if (period1 < period1Minimum) {
        return undefined;
    }
    return Math.max(period2Minimum, splitTotal - period1);
};

// is splitting. record logs since first split
export const recordSplitLogsSymmetric = (state: IState, splitInfo: ISplitInfo) => {
    if (state.workday && splitInfo.periods.length > 0) {
        appendCurrentSplitLog(state);
    }
};
